import React, { useEffect, useState } from "react";
import { api, fmt } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { Gift } from "lucide-react";

const PRIZE_COLORS = ["#a40606","#d4af37","#2d6a4f","#1a4480","#6b0000","#0b4a26","#8B5CF6","#F59E0B"];

export default function DailyWheel() {
  const { lang, updateBalance } = useApp();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/wheel/status").then(r=>setStatus(r.data)).catch(()=>{});
  }, []);

  const spin = async () => {
    if (!status?.available || spinning) return;
    setSpinning(true); setResult(null);
    try {
      const r = await api.post("/wheel/spin");
      const idx = r.data.prize_index;
      const slice = 360 / r.data.prizes.length;
      // Land on the chosen index; +5 full rotations
      const target = 360*6 - (idx*slice + slice/2);
      setRotation(prev => prev + target);
      setTimeout(()=>{
        setResult(r.data);
        updateBalance("HUF", r.data.balance_huf);
        toast.success(`+${r.data.prize.toLocaleString()} Ft`);
        setSpinning(false);
        api.get("/wheel/status").then(rr=>setStatus(rr.data)).catch(()=>{});
      }, 4500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Try again tomorrow");
      setSpinning(false);
    }
  };

  if (!status) return <div className="p-20 text-center text-white/40">Loading…</div>;

  const prizes = status.prizes;
  const sliceDeg = 360 / prizes.length;
  const nextAt = status.next_at ? new Date(status.next_at) : null;

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-10 text-center">
      <h1 className="font-serif text-4xl gold-text mb-2 flex items-center justify-center gap-3"><Gift/>Daily Wheel</h1>
      <p className="text-white/50 mb-10 text-sm">Free spin every 24 hours · HUF prizes</p>

      <div className="relative w-[400px] h-[400px] mx-auto mb-10">
        {/* Pointer */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10" style={{width:0,height:0,borderLeft:"18px solid transparent",borderRight:"18px solid transparent",borderTop:"32px solid #d4af37",filter:"drop-shadow(0 4px 6px rgba(0,0,0,0.5))"}}/>
        <div className="w-full h-full rounded-full border-[10px] border-[#d4af37] relative overflow-hidden jackpot-glow" style={{transform:`rotate(${rotation}deg)`, transition: spinning?"transform 4.3s cubic-bezier(.17,.67,.32,1)":"none"}}>
          {prizes.map((p, i) => (
            <div key={i} className="absolute inset-0" style={{transform:`rotate(${i*sliceDeg}deg)`}}>
              <div className="absolute top-0 left-1/2 origin-bottom h-1/2 w-[140px] -translate-x-1/2 flex items-start justify-center pt-6 font-serif text-white text-base"
                style={{background:`linear-gradient(180deg, ${PRIZE_COLORS[i%PRIZE_COLORS.length]} 0%, ${PRIZE_COLORS[i%PRIZE_COLORS.length]}99 100%)`,
                clipPath:`polygon(50% 100%, 0% 0%, 100% 0%)`}}>
                <div className="text-shadow font-bold" style={{transform:`rotate(${sliceDeg/2}deg)`}}>
                  {p>=1000?`${p/1000}K`:p}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-black border-4 border-[#d4af37] flex items-center justify-center z-10 gold-text font-serif text-2xl">
          IC
        </div>
      </div>

      {status.available ? (
        <button data-testid="wheel-spin" onClick={spin} disabled={spinning} className="btn-gold px-12 py-5 rounded-md text-base">{spinning?"…":"SPIN"}</button>
      ) : (
        <div data-testid="wheel-cooldown" className="text-white/50">
          Next free spin: <span className="text-[#d4af37]">{nextAt?.toLocaleString()}</span>
        </div>
      )}

      {result && (
        <div data-testid="wheel-result" className="mt-8 lux-card jackpot-glow p-6 inline-block">
          <div className="text-xs uppercase tracking-widest text-[#d4af37]">You won</div>
          <div className="font-serif text-4xl gold-text">{result.prize.toLocaleString()} Ft</div>
        </div>
      )}
    </div>
  );
}
